import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { ScrollArea, ScrollBar } from "./ui/scroll-area";
import { Button } from "./ui/button";
import { FeaturedRestaurantCard } from "./featured/FeaturedRestaurantCard";
import { useFeaturedRestaurants } from "@/hooks/useFeaturedRestaurants";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useRef, useCallback, memo } from "react";

const ScrollControls = memo(({ onScroll, canScrollLeft, canScrollRight }: {
  onScroll: (direction: 'left' | 'right') => void;
  canScrollLeft: boolean;
  canScrollRight: boolean;
}) => (
  <div className="flex gap-1.5">
    <Button
      variant="outline"
      size="icon"
      className="h-8 w-8 rounded-full border-border/60 hover:border-secondary hover:text-secondary transition-colors"
      onClick={() => onScroll('left')}
      disabled={!canScrollLeft}
    >
      <ChevronLeft className="h-4 w-4" />
    </Button>
    <Button
      variant="outline"
      size="icon"
      className="h-8 w-8 rounded-full border-border/60 hover:border-secondary hover:text-secondary transition-colors"
      onClick={() => onScroll('right')}
      disabled={!canScrollRight}
    >
      <ChevronRight className="h-4 w-4" />
    </Button>
  </div>
));

ScrollControls.displayName = 'ScrollControls';

export const FeaturedRestaurants = () => {
  const isMobile = useIsMobile();
  const { data: featuredRestaurants, isLoading } = useFeaturedRestaurants();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !featuredRestaurants || featuredRestaurants.length <= 3) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, [featuredRestaurants]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    
    const scrollAmount = direction === 'left' ? -400 : 400;
    scrollContainerRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 px-1">
          <div className="h-6 w-40 bg-muted animate-pulse rounded" />
        </div>
        <div className="flex gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`flex-none ${isMobile ? 'w-[300px]' : 'w-[380px]'} rounded-xl overflow-hidden`}>
              <div className={`${isMobile ? 'h-48' : 'h-56'} bg-muted animate-pulse`} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!featuredRestaurants || featuredRestaurants.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2.5">
          <Star className="h-4 w-4 text-secondary fill-secondary" />
          <h2 className={`font-display font-semibold ${isMobile ? 'text-xl' : 'text-2xl'} text-foreground`}>
            Featured Venues
          </h2>
        </div>
        {!isMobile && featuredRestaurants.length > 3 && (
          <ScrollControls 
            onScroll={scroll}
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
          />
        )}
      </div>
      
      <ScrollArea className="w-full whitespace-nowrap -mx-1">
        <div 
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex w-full gap-4 pb-3 px-1"
        >
          {featuredRestaurants.map((restaurant, index) => (
            <div 
              key={restaurant.id} 
              className={`flex-none ${isMobile ? 'w-[300px]' : 'w-[380px]'}`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <FeaturedRestaurantCard restaurant={restaurant} />
            </div>
          ))}
        </div>
        <ScrollBar orientation="horizontal" className="opacity-0" />
      </ScrollArea>
    </div>
  );
};
